import { Tour, TourObjectTask, TourObjectTaskStatus } from "../types"

export type IsoWeekRef = { year: number; week: number }

export const compareIsoWeek = (a: IsoWeekRef, b: IsoWeekRef) => {
    if (a.year !== b.year) return a.year - b.year
    return a.week - b.week
}

export const taskWeek = (task: TourObjectTask): IsoWeekRef => ({ year: task.dueYear, week: task.dueWeekNumber })

export const isActionableStatus = (status: TourObjectTaskStatus) => status === "open" || status === "rejected"

export const partitionObjectTasks = (tasks: TourObjectTask[], current: IsoWeekRef) => {
    const dueNow = tasks
        .filter((task) => task.status !== "dismissed" && compareIsoWeek(taskWeek(task), current) <= 0)
        .sort(
            (a, b) =>
                Number(isActionableStatus(b.status)) - Number(isActionableStatus(a.status)) ||
                compareIsoWeek(taskWeek(a), taskWeek(b)) ||
                a.activity.localeCompare(b.activity),
        )
    const upcoming = tasks
        .filter((task) => task.status !== "dismissed" && compareIsoWeek(taskWeek(task), current) > 0)
        .sort((a, b) => compareIsoWeek(taskWeek(a), taskWeek(b)) || a.activity.localeCompare(b.activity))
    return { dueNow, upcoming }
}

export type ObjectTaskGroup = {
    key: string
    activity: string
    description?: string
    dueNow: TourObjectTask[]
    upcoming: TourObjectTask[]
}

const groupKey = (task: TourObjectTask) => task.catalogItem || task.activity

export const groupObjectTaskSeries = (dueNow: TourObjectTask[], upcoming: TourObjectTask[]): ObjectTaskGroup[] => {
    const groups = new Map<string, ObjectTaskGroup>()
    const add = (task: TourObjectTask, bucket: "dueNow" | "upcoming") => {
        const key = groupKey(task)
        const existing = groups.get(key)
        if (!existing) {
            groups.set(key, {
                key,
                activity: task.activity,
                description: task.description,
                dueNow: bucket === "dueNow" ? [task] : [],
                upcoming: bucket === "upcoming" ? [task] : [],
            })
            return
        }
        if (!existing.description && task.description) existing.description = task.description
        existing[bucket].push(task)
    }
    dueNow.forEach((task) => add(task, "dueNow"))
    upcoming.forEach((task) => add(task, "upcoming"))
    return Array.from(groups.values()).sort((a, b) => {
        if (a.dueNow.length > 0 && b.dueNow.length === 0) return -1
        if (a.dueNow.length === 0 && b.dueNow.length > 0) return 1
        return a.activity.localeCompare(b.activity)
    })
}

export const groupTasksByObject = (tasks: TourObjectTask[]) => {
    const map = new Map<string, TourObjectTask[]>()
    for (const task of tasks) {
        const objectId = task.object?._id ?? ""
        const list = map.get(objectId) ?? []
        list.push(task)
        map.set(objectId, list)
    }
    return map
}

export const weekLabel = (task: TourObjectTask) => `KW ${task.dueWeekNumber}`

/** Offene (inkl. abgelehnte) und erledigte Aufgaben der Tour-Objekte in der geladenen Wochenmenge. */
export const countTourWeekTasks = (tour: Tour, tasksByObject: Map<string, TourObjectTask[]>) => {
    const seen: Record<string, true> = {}
    let open = 0
    let done = 0
    tour.stops.forEach((stop) => {
        const objectId = stop.object?._id
        if (!objectId || seen[objectId]) return
        seen[objectId] = true
        const tasks = tasksByObject.get(objectId) ?? []
        tasks.forEach((task) => {
            if (task.status === "dismissed") return
            if (task.status === "done") done += 1
            else open += 1
        })
    })
    return { open, done }
}
